import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl, Title } from '@angular/platform-browser';
// import { NavigationEnd, Router } from '@angular/router';
import {
  AuthBrandingService,
  // AuthSessionService,
  tenantConfig$,
} from '@mbai/multi-tenant-common-auth-ng';
import { catchError, distinctUntilChanged, filter, map, of, switchMap } from 'rxjs';
import { getQueryParams } from '../../shared/utils/query-params.util';
import { environment } from '../../../environments/environment';
import type { IPartnerBrandingApiItem, IPartnerFilePaths } from '../models/partner-branding.model';
import { AppThemeService } from './app-theme.service';

function trimNonEmpty(value: string | null | undefined): string | null {
  if (value == null || typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function isPartnerFilePaths(
  filePaths: IPartnerFilePaths | readonly unknown[] | null | undefined,
): filePaths is IPartnerFilePaths {
  return filePaths !== null && filePaths !== undefined && !Array.isArray(filePaths);
}

function partnerFilePath(
  filePaths: IPartnerFilePaths | readonly unknown[] | null | undefined,
  key: keyof IPartnerFilePaths,
): string | null {
  if (!isPartnerFilePaths(filePaths)) {
    return null;
  }
  return trimNonEmpty(filePaths[key]);
}

function isHttpsUrl(url: string): boolean {
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
}

@Injectable({ providedIn: 'root' })
export class PartnerBrandingService {
  private readonly http = inject(HttpClient);
  private readonly authBranding = inject(AuthBrandingService);
  private readonly appTheme = inject(AppThemeService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly title = inject(Title);
  // private readonly session = inject(AuthSessionService);
  // private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly partnerBrandingReady = signal(false);
  readonly partnerName = signal<string | null>(null);
  readonly productName = signal<string | null>(null);
  readonly userGuidePdfUrl = signal<SafeResourceUrl | null>(null);
  readonly userGuideVideoUrl = signal<SafeResourceUrl | null>(null);
  readonly userGuideVideoThumbnailUrl = signal<string | null>(null);

  constructor() {
    tenantConfig$
      .pipe(
        filter((cfg) => !!cfg?.realm?.trim()),
        map((cfg) => cfg!.realm.trim()),
        distinctUntilChanged(),
        switchMap((realm) => {
          this.partnerBrandingReady.set(false);
          return this.fetchPartnerBranding(realm);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((partner) => this.applyPartnerBranding(partner));

    // Future: refresh tab title when org changes
    // this.router.events
    //   .pipe(
    //     filter((event) => event instanceof NavigationEnd),
    //     takeUntilDestroyed(this.destroyRef),
    //   )
    //   .subscribe(() => this.updateDocumentTitle());
  }

  updateDocumentTitle(): void {
    const partnerName = this.partnerName();
    if (!partnerName) {
      this.title.setTitle('');
      return;
    }

    this.title.setTitle(partnerName);
    // Future: include organization display name in tab title
    // const displayName = trimNonEmpty(this.session.activeOrgDisplayName);
    // this.title.setTitle(displayName ? `${partnerName} - ${displayName}` : partnerName);
  }

  private fetchPartnerBranding(realm: string) {
    const apiUrl = environment.dataLensAPI;
    if (!apiUrl) {
      return of(null);
    }
    const url = `${apiUrl}/api/v1/data-lense/partners?${getQueryParams({ realm_name: realm })}`;
    return this.http.get<IPartnerBrandingApiItem[]>(url).pipe(
      map((items) => (Array.isArray(items) && items.length > 0 ? items[0] : null)),
      catchError(() => of(null)),
    );
  }

  private applyPartnerBranding(partner: IPartnerBrandingApiItem | null): void {
    const filePaths = partner?.file_paths;
    this.productName.set(trimNonEmpty(partner?.product_name));
    this.userGuidePdfUrl.set(this.toSafeResourceUrl(partnerFilePath(filePaths, 'partner_guide_pdf')));
    this.userGuideVideoUrl.set(this.toSafeResourceUrl(partnerFilePath(filePaths, 'partner_guide_video')));
    this.userGuideVideoThumbnailUrl.set(
      this.toTrustedHttpsUrl(partnerFilePath(filePaths, 'partner_guide_video_thumbnail')),
    );

    if (!partner) {
      this.partnerName.set(null);
      this.appTheme.applyThemeColors(null);
      this.authBranding.setBranding({
        logoUrl: null,
        logoAlt: null,
        footerText: null,
      });
      this.updateDocumentTitle();
      this.partnerBrandingReady.set(true);
      return;
    }

    const partnerName = trimNonEmpty(partner.partner_name);
    const logoUrl = this.toTrustedHttpsUrl(partnerFilePath(filePaths, 'logo'));
    const faviconUrl = this.toTrustedHttpsUrl(partnerFilePath(filePaths, 'favicon'));

    this.partnerName.set(partnerName);

    this.appTheme.applyThemeColors(partner.theme_colors);

    const footerText = partnerName ? `Powered by ${partnerName}` : null;

    this.authBranding.setBranding({
      logoUrl,
      logoAlt: partnerName,
      footerText,
    });

    if (faviconUrl) {
      this.setFavicon(faviconUrl);
    }

    this.updateDocumentTitle();
    this.partnerBrandingReady.set(true);
  }

  private toSafeResourceUrl(url: string | null): SafeResourceUrl | null {
    if (!url || !isHttpsUrl(url)) {
      return null;
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  private toTrustedHttpsUrl(url: string | null): string | null {
    if (!url || !isHttpsUrl(url)) {
      return null;
    }
    return url;
  }

  private setFavicon(url: string): void {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = url;
  }
}
